// Google Maps API types
interface GoogleMapsAPI {
  maps: {
    places: Record<string, unknown>;
    importLibrary: (name: string) => Promise<Record<string, unknown>>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// Google Maps type declarations (inline to avoid module resolution issues)
declare global {
  interface Window {
    google?: GoogleMapsAPI;
    __googleMapsCallback?: () => void;
  }
}

/**
 * Google Maps JavaScript API loader utility
 * Dynamically loads the Google Maps JavaScript API script
 */

export interface GoogleMapsLoaderConfig {
  apiKey: string;
  libraries?: string[];
  version?: string;
  language?: string;
  region?: string;
}

let isLoading = false;
let isLoaded = false;
let loadPromise: Promise<void> | null = null;

/**
 * Load Google Maps JavaScript API
 * @param config - Configuration for the Google Maps API
 * @returns Promise that resolves when the API is loaded
 */
export function loadGoogleMapsAPI(
  config: GoogleMapsLoaderConfig,
): Promise<void> {
  // Return existing promise if already loading
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Return resolved promise if already loaded
  if (isLoaded) {
    return Promise.resolve();
  }

  // Check if already loaded in window
  if (typeof window !== 'undefined' && window.google && window.google?.maps) {
    isLoaded = true;
    return Promise.resolve();
  }

  isLoading = true;

  loadPromise = new Promise<void>((resolve, reject) => {
    // Create script element
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;

    // Build URL with parameters
    const params = new URLSearchParams({
      key: config.apiKey,
      libraries: (config.libraries || ['places']).join(','),
      v: config.version || 'weekly',
      callback: '__googleMapsCallback',
      loading: 'async', // Required to suppress "loaded directly" warning & improve perf
    });

    if (config.language) {
      params.set('language', config.language);
    }
    if (config.region) {
      params.set('region', config.region);
    }

    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;

    // Set up global callback
    window.__googleMapsCallback = () => {
      isLoaded = true;
      isLoading = false;
      delete window.__googleMapsCallback;
      resolve();
    };

    // Handle script errors
    script.onerror = () => {
      isLoading = false;
      loadPromise = null;
      delete window.__googleMapsCallback;
      reject(new Error('Failed to load Google Maps JavaScript API'));
    };

    // Append script to head
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Check if Google Maps API is loaded
 * @returns boolean indicating if the API is loaded
 */
export function isGoogleMapsLoaded(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.google &&
    !!window.google?.maps &&
    !!window.google?.maps?.places
  );
}
